import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import axios from "../axios";
import { AdminAddButton, AdminFormActions, AdminFormModal, RecordActions } from "./AdminForm";
import { removePrompt, useConfirm } from "./ConfirmDialog";
import Message from "./Message";

function authConfig(token, json = false) {
  const headers = { Authorization: `Bearer ${token}` };
  if (json) {
    headers["Content-Type"] = "application/json";
  }
  return { headers };
}

function errorText(error) {
  return error.response && error.response.data.detail
    ? error.response.data.detail
    : error.message;
}

function photoName(photo) {
  return photo.caption.trim() || "this show photo";
}

function ShowSection({ title, open, onToggle, children }) {
  return (
    <section className={open ? "home-admin-card show-admin-card is-open" : "home-admin-card show-admin-card"}>
      <h3>
        <button type="button" aria-expanded={open} onClick={onToggle}>
          {title}
          <span className="home-admin-chevron" aria-hidden="true" />
        </button>
      </h3>
      <div className="accordion-fold">
        <div className="accordion-panel" inert={!open}>
          <div className="home-admin-panel">{children}</div>
        </div>
      </div>
    </section>
  );
}

function AdminShow({ token }) {
  const [photos, setPhotos] = useState([]);
  const [files, setFiles] = useState([]);
  const [fileKey, setFileKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [open, setOpen] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [captionPhoto, setCaptionPhoto] = useState(null);
  const [captionDraft, setCaptionDraft] = useState("");
  const [ask, confirmDialog] = useConfirm();

  useEffect(() => {
    axios
      .get("/api/admin/show-photos/", authConfig(token))
      .then(({ data }) => {
        setPhotos(data);
      })
      .catch((err) => setError(errorText(err)));
  }, [token]);

  const upload = async (event) => {
    event.preventDefault();
    if (!files.length) {
      return;
    }
    setBusy(true);
    setError("");
    setNotice("");
    const formData = new FormData();
    files.forEach((file) => formData.append("photos", file));
    try {
      const { data } = await axios.post("/api/admin/show-photos/", formData, authConfig(token));
      const added = Array.isArray(data) ? data : data.photos || [];
      setPhotos((current) => [...current, ...added]);
      setFiles([]);
      setFileKey((value) => value + 1);
      setAddOpen(false);
      setNotice(data.detail || "Photos added.");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const saveCaption = async (photo) => {
    setError("");
    setNotice("");
    try {
      const { data } = await axios.patch(
        `/api/admin/show-photos/${photo.id}/`,
        { caption: photo.caption },
        authConfig(token, true)
      );
      setPhotos((current) => current.map((item) => (item.id === photo.id ? data : item)));
      setNotice("Caption saved.");
    } catch (err) {
      setError(errorText(err));
    }
  };

  const remove = async (photo) => {
    const name = photoName(photo);
    if (!(await ask(removePrompt(name)))) {
      return;
    }
    setError("");
    setNotice("");
    try {
      await axios.delete(`/api/admin/show-photos/${photo.id}/`, authConfig(token));
      setPhotos((current) => current.filter((item) => item.id !== photo.id));
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <>
      {confirmDialog}
      <h2>Show page</h2>
      {error ? <Message variant="danger">{error}</Message> : null}
      {notice ? <Message variant="info">{notice}</Message> : null}
      <ShowSection title="Hero gallery" open={open} onToggle={() => setOpen((current) => !current)}>
          <p className="field-note">
            Photos for the gallery at the top of The Show. Each one is cropped from the center into a wide
            rectangle, three times as wide as it is tall, so it fills that banner. Until a photo is added here,
            the page still gathers pictures from events, luncheon logos, and class winners.
          </p>
          <AdminAddButton onClick={() => setAddOpen(true)}>Add photos</AdminAddButton>
          <AdminFormModal show={addOpen} title="Add photos" onHide={() => setAddOpen(false)}>
          <Form className="mt-3" onSubmit={upload}>
            <Form.Group className="mb-3" controlId="showPhotos">
              <Form.Label>Photos</Form.Label>
              <Form.Control
                key={fileKey}
                type="file"
                accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                multiple
                onChange={(event) => setFiles(Array.from(event.target.files || []))}
              />
            </Form.Group>
          <AdminFormActions
            saveLabel={busy ? "Uploading" : "Add photos"}
            busy={busy}
            disabled={files.length === 0}
            onCancel={() => setAddOpen(false)}
          />
          </Form>
          </AdminFormModal>
          <AdminFormModal
            show={Boolean(captionPhoto)}
            title="Edit caption"
            onHide={() => setCaptionPhoto(null)}
          >
            <Form
              onSubmit={async (event) => {
                event.preventDefault();
                await saveCaption({ ...captionPhoto, caption: captionDraft });
                setCaptionPhoto(null);
              }}
            >
              <Form.Group className="mb-3" controlId="showCaption">
                <Form.Label>Caption</Form.Label>
                <Form.Control
                  value={captionDraft}
                  maxLength={160}
                  onChange={(event) => setCaptionDraft(event.target.value)}
                />
              </Form.Group>
              <AdminFormActions saveLabel="Save caption" onCancel={() => setCaptionPhoto(null)} />
            </Form>
          </AdminFormModal>
          {photos.length === 0 ? (
            <p className="field-note mt-3">No show photos yet.</p>
          ) : (
            <div className="admin-photo-grid">
              {photos.map((photo) => {
                const name = photoName(photo);
                return (
                  <article key={photo.id} className="admin-photo-card">
                    <img src={photo.thumb_url} alt={photo.caption.trim() || "Show gallery photo"} />
                    <p>{photo.caption.trim() || "No caption yet"}</p>
                    <RecordActions
                      name={name}
                      onEdit={() => {
                        setCaptionPhoto(photo);
                        setCaptionDraft(photo.caption || "");
                      }}
                      onRemove={() => remove(photo)}
                    />
                  </article>
                );
              })}
            </div>
          )}
      </ShowSection>
    </>
  );
}

export default AdminShow;
